import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, KeyRound, AlertTriangle } from 'lucide-react';
import * as ownerApi from '@/api/owner.api';
import * as consumosApi from '@/api/consumos.api';
import { Card, CardBody, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/hooks/useToast';

/**
 * Límites de gasto (F2 — solo OWNER). Topes diarios del guard de gasto de la
 * API de Anthropic, editables sin redeploy. Cada cambio exige la clave de
 * operación (segunda clave, independiente del login). El guard NUNCA corta el
 * servicio del cliente por sí solo: bloquea nuevas llamadas a la IA y avisa
 * al dueño; los mensajes quedan en bandeja para un humano.
 */

const usd = (n: number | null | undefined) =>
  n == null ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function OrigenBadge({ origen }: { origen: ownerApi.SpendLimitOrigin }) {
  return origen === 'db' ? (
    <Badge variant="brand">editado en panel</Badge>
  ) : (
    <Badge variant="default">default del sistema</Badge>
  );
}

function GastoHoyCard({ view }: { view: ownerApi.SpendLimitsView }) {
  const gasto = view.gastoHoyUsd ?? 0;
  const tope = Math.min(
    view.efectivos.installationDailyCapUsd,
    view.efectivos.globalDailyCapUsd,
  );
  const pct = tope > 0 ? Math.min(100, Math.round((gasto / tope) * 100)) : 0;
  const enRojo = pct >= 100;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Gasto de hoy</CardTitle>
        <CardDescription>
          Consumo del día en la API de Anthropic contra el tope diario más ajustado.
        </CardDescription>
      </CardHeader>
      <CardBody className="space-y-3">
        <div className="flex items-baseline justify-between">
          <span className="text-2xl font-bold text-slate-800 dark:text-slate-100">
            {usd(view.gastoHoyUsd)}
          </span>
          <span className="text-sm text-slate-400">de {usd(tope)} ({pct}%)</span>
        </div>
        <div className="h-3 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
          <div
            className={
              'h-full rounded-full transition-all ' +
              (enRojo ? 'bg-danger' : pct >= 75 ? 'bg-amber-500' : 'bg-brand-600')
            }
            style={{ width: `${pct}%` }}
          />
        </div>
        {!view.efectivos.enabled && (
          <p className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4" /> El guard de gasto está APAGADO: no hay tope activo.
          </p>
        )}
      </CardBody>
    </Card>
  );
}

function LimitesCard({ view }: { view: ownerApi.SpendLimitsView }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [instalacion, setInstalacion] = useState('');
  const [global, setGlobal] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [clave, setClave] = useState('');

  useEffect(() => {
    setInstalacion(String(view.efectivos.installationDailyCapUsd));
    setGlobal(String(view.efectivos.globalDailyCapUsd));
    setEnabled(view.efectivos.enabled);
  }, [view]);

  const save = useMutation({
    mutationFn: () =>
      ownerApi.putSpendLimits(
        {
          enabled,
          installationDailyCapUsd: Number(instalacion),
          globalDailyCapUsd: Number(global),
        },
        clave,
      ),
    onSuccess: () => {
      setClave('');
      void qc.invalidateQueries({ queryKey: ['owner', 'spend-limits'] });
      toast.success('Límites actualizados');
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'No se pudieron actualizar los límites'),
  });

  const numerosValidos = Number(instalacion) > 0 && Number(global) > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Topes diarios (USD)</CardTitle>
        <CardDescription>
          Cada cambio exige la clave de operación. Al llegar al tope, el agente deja de llamar a
          la IA y te avisa — los chats quedan en bandeja para atención humana.
        </CardDescription>
      </CardHeader>
      <CardBody className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <Input
              label="Tope diario por instalación"
              type="number"
              min="0.5"
              step="0.5"
              value={instalacion}
              onChange={(e) => setInstalacion(e.target.value)}
            />
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <OrigenBadge origen={view.origen.installationDailyCapUsd} />
              <span>default: {usd(view.envDefaults.installationDailyCapUsd)}</span>
            </div>
          </div>
          <div className="space-y-1">
            <Input
              label="Tope diario global"
              type="number"
              min="0.5"
              step="0.5"
              value={global}
              onChange={(e) => setGlobal(e.target.value)}
            />
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <OrigenBadge origen={view.origen.globalDailyCapUsd} />
              <span>default: {usd(view.envDefaults.globalDailyCapUsd)}</span>
            </div>
          </div>
        </div>

        <label className="flex cursor-pointer items-center justify-between gap-4 py-1">
          <div>
            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">Guard de gasto activo</p>
            <p className="text-xs text-slate-400">
              Apagarlo deja la API de Anthropic SIN tope diario. Solo para emergencias.
            </p>
          </div>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-5 w-5 accent-brand-600"
          />
        </label>

        <div className="rounded-lg border border-surface-border p-4 dark:border-slate-800">
          <Input
            label="Clave de operación (requerida para guardar)"
            type="password"
            placeholder="••••••••"
            value={clave}
            onChange={(e) => setClave(e.target.value)}
          />
        </div>

        <div>
          <Button
            onClick={() => save.mutate()}
            loading={save.isPending}
            disabled={!numerosValidos || clave.length === 0}
          >
            <ShieldCheck className="h-4 w-4" /> Guardar límites
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function CupoMensualCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: meter, isLoading } = useQuery({
    queryKey: ['consumos', 'meter'],
    queryFn: consumosApi.getMeter,
  });
  const [cupo, setCupo] = useState('');
  const [clave, setClave] = useState('');

  useEffect(() => {
    if (meter) setCupo(String(meter.cupo));
  }, [meter]);

  const save = useMutation({
    mutationFn: () => ownerApi.putConversationQuota(Number(cupo), clave),
    onSuccess: () => {
      setClave('');
      void qc.invalidateQueries({ queryKey: ['consumos', 'meter'] });
      toast.success('Cupo mensual actualizado');
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'No se pudo actualizar el cupo'),
  });

  if (isLoading || !meter) return <Spinner />;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cupo mensual del cliente</CardTitle>
        <CardDescription>
          Conversaciones incluidas en el plan. INFORMATIVO: pasarse pone el medidor en rojo y
          te avisa — el servicio del cliente nunca se corta.
        </CardDescription>
      </CardHeader>
      <CardBody className="space-y-4">
        <div className="flex items-baseline justify-between">
          <span className="text-2xl font-bold text-slate-800 dark:text-slate-100">
            {meter.conversaciones.toLocaleString()}
          </span>
          <span className={meter.excedido ? 'text-sm font-medium text-danger' : 'text-sm text-slate-400'}>
            de {meter.cupo.toLocaleString()} ({meter.pct}%)
          </span>
        </div>
        <div className="h-3 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
          <div
            className={
              'h-full rounded-full transition-all ' +
              (meter.excedido ? 'bg-danger' : meter.pct >= 75 ? 'bg-amber-500' : 'bg-brand-600')
            }
            style={{ width: `${Math.min(100, meter.pct)}%` }}
          />
        </div>
        <Input
          label="Cupo mensual (conversaciones)"
          type="number"
          min="1"
          step="100"
          value={cupo}
          onChange={(e) => setCupo(e.target.value)}
        />
        <Input
          label="Clave de operación (requerida para guardar)"
          type="password"
          placeholder="••••••••"
          value={clave}
          onChange={(e) => setClave(e.target.value)}
        />
        <div>
          <Button
            onClick={() => save.mutate()}
            loading={save.isPending}
            disabled={Number(cupo) <= 0 || clave.length === 0}
          >
            Guardar cupo
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function ClaveOperacionCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['owner', 'operation-key'],
    queryFn: ownerApi.getOperationKey,
  });
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');

  const save = useMutation({
    mutationFn: () => ownerApi.putOperationKey(nueva, actual || undefined),
    onSuccess: () => {
      setActual('');
      setNueva('');
      void qc.invalidateQueries({ queryKey: ['owner', 'operation-key'] });
      toast.success('Clave de operación guardada');
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'No se pudo guardar la clave'),
  });

  if (isLoading) return <Spinner />;
  const definida = data?.definida ?? false;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-4 w-4" /> Clave de operación
        </CardTitle>
        <CardDescription>
          Segunda clave, independiente del login. Se pide en cada cambio de límites.
          {definida ? ' Ya hay una definida.' : ' Todavía no está definida — sin ella no se pueden editar los límites.'}
        </CardDescription>
      </CardHeader>
      <CardBody className="space-y-4">
        {definida && (
          <Input
            label="Clave actual"
            type="password"
            value={actual}
            onChange={(e) => setActual(e.target.value)}
          />
        )}
        <Input
          label={definida ? 'Clave nueva' : 'Definir clave (mínimo 8 caracteres)'}
          type="password"
          value={nueva}
          onChange={(e) => setNueva(e.target.value)}
        />
        <div>
          <Button
            onClick={() => save.mutate()}
            loading={save.isPending}
            disabled={nueva.length < 8 || (definida && actual.length === 0)}
          >
            {definida ? 'Rotar clave' : 'Definir clave'}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

export default function Limites() {
  const { data: view, isLoading } = useQuery({
    queryKey: ['owner', 'spend-limits'],
    queryFn: ownerApi.getSpendLimits,
  });

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Límites de gasto"
        description="Topes diarios de la API de Anthropic. Reservado del dueño del sistema."
      />
      {isLoading || !view ? (
        <Spinner />
      ) : (
        <div className="space-y-4">
          <GastoHoyCard view={view} />
          <div className="grid gap-4 lg:grid-cols-2">
            <LimitesCard view={view} />
            <ClaveOperacionCard />
            <CupoMensualCard />
          </div>
        </div>
      )}
    </div>
  );
}
